import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Lister from "../../components/Lister/Lister";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import { useSession } from "../../contexts/SessionContext";
import { EphemeralUserModel } from "../../models/EphemeralUser";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { EphemeralUserListItem } from "./EphemeralUsers.d";
import { fetchEphemeralUsers } from "./EphemeralUsers.function";

const _getSucceedLabel = (language: string, succeed?: boolean | null) => {
  if (succeed === true) return translate(language, "PAGES.EPHEMERAL_USERS.SUCCESS");
  if (succeed === false) return translate(language, "PAGES.EPHEMERAL_USERS.FAILED");

  return translate(language, "PAGES.EPHEMERAL_USERS.PENDING");
};

export const EphemeralUsers = () => {
  const { language } = useSession();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [ephemeralUsers, setEphemeralUsers] = useState<EphemeralUserModel[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchEphemeralUsers(setEphemeralUsers, setLoaded, language);
  }, [language]);

  const normalizedItems: EphemeralUserListItem[] = useMemo(
    () =>
      ephemeralUsers.map((item) => ({
        ID: item.ID,
        dt_created: item.dt_created,
        email: item.email || "-",
        name: item.name || "-",
        session_id: item.session_id || "-",
        succeed: _getSucceedLabel(language, item.succeed),
        raw_succeed: item.succeed ?? null,
      })),
    [ephemeralUsers, language]
  );

  const filteredItems = useMemo(
    () =>
      normalizedItems.filter((item) =>
        JSON.stringify({
          ID: item.ID,
          email: item.email,
          name: item.name,
          session_id: item.session_id,
          succeed: item.succeed,
        })
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ),
    [normalizedItems, searchQuery]
  );

  const renderSucceedBadge = (rawSucceed: boolean | null) => {
    if (rawSucceed === true) {
      return <span className="badge bg-success">{translate(language, "PAGES.EPHEMERAL_USERS.SUCCESS")}</span>;
    }

    if (rawSucceed === false) {
      return <span className="badge bg-danger">{translate(language, "PAGES.EPHEMERAL_USERS.FAILED")}</span>;
    }

    return <span className="badge bg-warning text-dark">{translate(language, "PAGES.EPHEMERAL_USERS.PENDING")}</span>;
  };

  const columns = [
    {
      header: translate(language, "GLOBAL.ID"),
      key: "ID",
      sortType: "string",
      sortable: true,
      render: (item: EphemeralUserListItem) => (
        <span
          className="btn-link p-0 text-start text-decoration-none"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/ephemeral/users/${item.ID}`);
          }}
          title={item.ID}
        >
          {item.ID}
        </span>
      ),
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_USERS.CREATED_AT"),
      key: "dt_created",
      sortType: "date",
      sortable: true,
      render: (item: EphemeralUserListItem) => (
        <span title={formatDateHumanly(language, new Date(item.dt_created * 1000).toISOString(), true)}>
          {formatDateHumanly(language, new Date(item.dt_created * 1000).toISOString(), true)}
        </span>
      ),
    },
    {
      header: translate(language, "GLOBAL.EMAIL"),
      key: "email",
      sortType: "string",
      sortable: true,
      render: (item: EphemeralUserListItem) => <span>{item.email}</span>,
    },
    {
      header: translate(language, "GLOBAL.NAME"),
      key: "name",
      sortType: "string",
      sortable: true,
      render: (item: EphemeralUserListItem) => <strong>{item.name}</strong>,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_USERS.SESSION_ID"),
      key: "session_id",
      sortType: "string",
      sortable: true,
      render: (item: EphemeralUserListItem) =>
        item.session_id !== "-" ? (
          <a
            href={`/ephemeral/sessions/${item.session_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
            onClick={(event) => event.stopPropagation()}
            title={item.session_id}
          >
            <code>{item.session_id}</code>
          </a>
        ) : (
          <span>-</span>
        ),
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_USERS.SUCCEED"),
      key: "succeed",
      sortType: "string",
      sortable: true,
      render: (item: EphemeralUserListItem) => renderSucceedBadge(item.raw_succeed),
    },
  ];

  return (
    <div className="m-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex w-fit align-items-center justify-content-between">
          <h2 className="mb-0 me-3">{translate(language, "PAGES.EPHEMERAL_USERS.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </div>
      </div>

      <Lister
        columns={columns}
        items={filteredItems}
        mock={!loaded}
        pagination
        paginationAlwaysShowFooter
        itemsPerPage={50}
        handleRowClick={(item: any) => navigate(`/ephemeral/users/${item.ID}`)}
      />
    </div>
  );
};

export default EphemeralUsers;
